import { ReactNode } from 'react';
import { useAuth } from '../../context/AuthContext';

interface PermissionGuardProps {
  children: ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: ReactNode;
}

/**
 * PermissionGuard component that conditionally renders children based on user permissions.
 *
 * @param permission - Single permission to check
 * @param permissions - Array of permissions to check
 * @param requireAll - If true, requires all permissions in the array (default: false, requires any)
 * @param fallback - Optional fallback component to render when permission check fails
 *
 * @example
 * <PermissionGuard permission="emissions:create">
 *   <CreateEmissionButton />
 * </PermissionGuard>
 *
 * @example
 * <PermissionGuard permissions={['emissions:update:all', 'emissions:delete:all']} requireAll>
 *   <AdminActions />
 * </PermissionGuard>
 */
export function PermissionGuard({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback = null,
}: PermissionGuardProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = useAuth();

  // Single permission check
  if (permission) {
    return hasPermission(permission) ? <>{children}</> : <>{fallback}</>;
  }

  // Multiple permissions check
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    return hasAccess ? <>{children}</> : <>{fallback}</>;
  }

  // No permissions specified, render children
  return <>{children}</>;
}
